const CANVAS_SIZE = 32
const BADGE_RADIUS_RATIO = 0.15

let canvas: HTMLCanvasElement | null = null
let ctx: CanvasRenderingContext2D | null = null
let originalTitle: string | null = null
let originalFaviconHref: string | null = null
const imageCache: Map<string, Promise<HTMLImageElement>> = new Map()

function getCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } | null {
  if (!canvas || !ctx) {
    canvas = document.createElement('canvas')
    canvas.width = CANVAS_SIZE
    canvas.height = CANVAS_SIZE
    ctx = canvas.getContext('2d')
    if (!ctx) return null
  }
  return { canvas, ctx }
}

function loadImage(href: string): Promise<HTMLImageElement> {
  const cached = imageCache.get(href)
  if (cached) return cached

  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = href
  })
  imageCache.set(href, promise)
  return promise
}

function getFaviconLink(): HTMLLinkElement {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  return link
}

function captureOriginals(): void {
  if (originalTitle === null) {
    originalTitle = document.title
  }
  if (originalFaviconHref === null) {
    const link = document.head.querySelector<HTMLLinkElement>('link[rel="icon"]')
    originalFaviconHref = link?.href ?? '/vectorFP3Logo.svg'
  }
}

export async function updateFaviconBadge(count: number, baseHref?: string): Promise<void> {
  if (count === 0) {
    if (originalFaviconHref !== null) {
      resetFaviconBadge()
    }
    return
  }

  captureOriginals()

  const canvasResult = getCanvas()
  if (!canvasResult) return
  const { canvas: cv, ctx: c } = canvasResult
  c.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)

  // fall back to whatever favicon was set at startup so env-tinted logos are preserved
  const effectiveBase = baseHref ?? originalFaviconHref ?? '/vectorFP3Logo.svg'

  try {
    const img = await loadImage(effectiveBase)
    c.drawImage(img, 0, 0, CANVAS_SIZE, CANVAS_SIZE)
  } catch {
    // draw a plain square as fallback when the image fails to load
    c.fillStyle = '#4f46e5'
    c.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE)
  }

  const radius = Math.round(CANVAS_SIZE * BADGE_RADIUS_RATIO)
  const cx = CANVAS_SIZE - radius - 1
  const cy = radius + 1

  c.beginPath()
  c.arc(cx, cy, radius + 1, 0, 2 * Math.PI)
  c.fillStyle = '#ffffff'
  c.fill()

  c.beginPath()
  c.arc(cx, cy, radius, 0, 2 * Math.PI)
  c.fillStyle = '#ef4444'
  c.fill()

  document.title = `(${count}) ${originalTitle}`

  const link = getFaviconLink()
  link.type = 'image/png'
  link.href = cv.toDataURL('image/png')
}

export function resetFaviconBadge(): void {
  if (originalTitle !== null) {
    document.title = originalTitle
    originalTitle = null
  }
  if (originalFaviconHref !== null) {
    const link = getFaviconLink()
    link.type = 'image/x-icon'
    link.href = originalFaviconHref
    originalFaviconHref = null
  }
}
